/*
 * File: WeSpeakReviewGuidelines.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 9, 2025 at 5:09 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 10:30 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import {
	Meta,
	AppNavigation,
	LegalNavigation,
	Typography,
	LocaleLink,
} from '../../../../../src/components';
import { HighlightSection } from '../../../../../src/components/LegalNavigation/LegalNavigation.styles';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../../constants/base.constants';
import { LEGAL_NAVIGATION_SECTIONS } from '../../Constants';

// Styles
import * as S from './WeSpeakReviewGuidelines.styles';


/**
 * Component
 */

const WeSpeakReviewGuidelines = ({ meta: m }) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageWeSpeakReviewGuidelines.metaTitle', { partnerName: partner.name }),
		description: t('pageWeSpeakReviewGuidelines.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create navigation sections
	const navigationSections = useMemo(() => LEGAL_NAVIGATION_SECTIONS(locale), [locale]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Component Content */}
			<AppNavigation showFooter contentPadding={false}>
				<LegalNavigation
					navigationSections={navigationSections}
					mobileNavigationHeight={440}
				>
					<S.Wrapper>

						{/* Title */}
						<Typography tag="h1" weight="bold">
							We-Speak Review Guidelines
						</Typography>

						{/* TL;DR */}
						<HighlightSection>
							<Typography tag="p">
								The We-Speak platform is powered by
								{' '}
								<LocaleLink
									title="Our Wave"
									aria-label="Our Wave"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									Our Wave
								</LocaleLink>
								, a nonprofit
								organization dedicated to building meaningful technology to empower survivors of sexual violence. For more
								information about our technology, team, or partnership opportunities, please
								{' '}
								<LocaleLink
									title="Visit Our Website"
									aria-label="Visit Our Website"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									visit our website
								</LocaleLink>
								.
							</Typography>
						</HighlightSection>

						{/* Overview */}
						<Typography tag="h2" weight="bold">Overview</Typography>
						<Typography tag="p">
							We-Speak is a project of Dublin Rape Crisis Centre. From time to time, we work with trusted partners to
							deliver the We-Speak platform.
						</Typography>
						<Typography tag="p">
							The purpose of these guidelines is to describe the ways in our review team reviews and posts survivor stories on
							our platform. This includes the rules we follow, the content we review, and a few of the reasons we decide to add
							trigger warnings, not post stories, or send resources to survivors.
						</Typography>
						<br />

						{/* Story Screening Preparation and Self-Care */}
						<Typography tag="h2" weight="bold">Redacting Identifiable Content</Typography>
						<Typography tag="p">
							Because the intention of We-Speak is to provide a safe and confidential space for survivors, our first job is to
							redact any information that can identify the survivor or may compromise survivor safety. Below is a step-by-step
							guide on how we redact content and what type of content is usually redacted.
						</Typography>
						<Typography tag="p">
							The We-Speak platform is made possible by the anonymity of the stories submitted. This community can only grow
							and support each other if all users do so in good faith and are conscious that any breach of anonymity, on
							another public platform of any kind could jeopardise the existence of We-Speak into the future.
						</Typography>
						<Typography tag="p">
							Under Irish law, all individuals have a Constitutional right to their good name. This right is asserted through defamation
							law. A defamatory statement is one which would lower a person&apos;s reputation in the eyes of society, and would cause others to
							strongly dislike or ridicule them (unless it is true or substantially true). Defamatory statements can be made online or offline.
						</Typography>
						<Typography tag="p">
							Contributors to the We-Speak website may also be active on social media. While it is very easy to publicise your own opinions
							and views on social media, please do not link what you upload to this site to your social media platforms. Think before you post,
							as you may be affecting somebody else’s reputation. This could in turn have a negative impact on your own life, the life of
							another and on the We-Speak platform.
						</Typography>
						<Typography tag="p">
							We trust that you will bear this in mind now and in the future, both on We-Speak and beyond, in order to empower this
							community to grow.
						</Typography>
						<Typography tag="p">
							We-Speak reserves the right to remove stories from the site, should we become aware that they are in breach of defamation
							law, or any of our other policies and guidelines.
						</Typography>
						<br />

						{/* Types of Content to be Redacted */}
						<h3>Types of Content to be Redacted</h3>
						<Typography tag="p">
							In general, we redact anything that could give a clue to a survivor’s identity or location. Just because an individual
							cannot identify them, doesn’t mean their friend or perpetrator might not be able to.
						</Typography>
						<br />

						{/* Names */}
						<Typography tag="h4" weight="bold">Names</Typography>
						<Typography tag="p">
							All first and last names are redacted from stories.
						</Typography>
						<ul>
							<li>
								If the name mentioned is the survivor’s name, we typically replace the name provided with “Survivor”
							</li>
							<li>
								If the name mentioned is the perpetrator’s name, we typically replace the name provided with “name”
							</li>
							<li>
								If the name mentioned is someone else in their life, we typically replace the name provided with who they
								are in relation to the survivor (i.e. friend, sibling, teacher, parent, etc.).
								<br />
							</li>
						</ul>
						<br />

						{/* Other Proper Nouns (e.g. locations, organizations, schools) */}
						<Typography tag="h4" weight="bold">Other Proper Nouns (e.g. locations, organizations, schools)</Typography>
						<Typography tag="p">
							Even if they are speaking positively about a place, we redact any proper noun and replace it with the type of noun it
							is (e.g. we replace “University College Dublin with “university”-- we replace “Galway” with “county”)
						</Typography>
						<br />

						{/* Specific Dates */}
						<Typography tag="h4" weight="bold">Specific Dates</Typography>
						<Typography tag="p">
							Specific dates will be redacted from stories to ensure they are not identifiable. (e.g. we replace “5th September 1992” with “1992”) and
							specifics such as “Christmas 1992” will be redacted.
						</Typography>
						<br />

						{/* Artwork */}
						<Typography tag="h4" weight="bold">Artwork</Typography>
						<Typography tag="p">
							In the case that a survivor submits a photography, realistic artwork, or other image depicting an easily identifiable person, we
							redact identifiable features (e.g. add a redaction blur to the face area). Even if the person’s face is not visible, it may still
							be necessary to redact other features or clothing of the person if they are particularly identifiable (e.g. unique costumes,
							name tags, etc.). The goal is to maintain the fidelity and purpose of the media while redacting identifiable components.
						</Typography>
						<br />

						{/* Decision To Not Post Stories */}
						<Typography tag="h2" weight="bold">Decision To Not Post Stories</Typography>
						<Typography tag="p">
							We-Speak reserves the right to not post stories that we deem are inappropriate or not applicable to our website. It
							is our responsibility to determine whether or not a story is appropriate for our page.
						</Typography>
						<br />

						{/* Story Not Appropriate */}
						<Typography tag="h4" weight="bold">Story Not Appropriate</Typography>
						<Typography tag="p">
							Stories that are not appropriate for We-Consent include:
						</Typography>
						<ol>
							<li>
								<b>Stories that belittle or put down survivor’s experiences</b>
								<br />
								Any story that talks negatively about survivors or blames them for their experiences
							</li>
							<li>
								<b>Stories that are obscene or vulgar</b>
								<br />
								Any story that has excessive graphic details or foul language
							</li>
							<li>
								<b>Stories that are considered “spam”</b>
								<br />
								Any submission where someone trying to advertise or sell something
							</li>
							<li>
								<b>Stories that appear to be “trolling”</b>
								<br />
								Any submission deliberately trying to evoke a negative reaction
							</li>
							<li>
								<b>Stories that the author explicitly states that they are under the age of 18</b>
							</li>
							<li>
								<b>Stories that are in relation to child sexual abuse, whether that be current or historic</b>
							</li>
						</ol>
						<Typography tag="p">
							If we believe a story may not be appropriate for our page, we will escalate the review to one of our trauma experts.
							While we want to represent a diversity of experiences, we want to be sure we are creating a safe environment for
							survivors to learn from each other.
						</Typography>
						<br />

						{/* Story Not Applicable */}
						<Typography tag="h4" weight="bold">Story Not Applicable</Typography>
						<Typography tag="p">
							Stories that are not applicable to We-Speak include:
						</Typography>
						<ol>
							<li>
								Any submission that does not relate in any way to sexual assault, trauma, violence, or harassment
							</li>
							<li>
								Submissions that do not have a logical flow or are difficult to read or understand
							</li>
						</ol>
						<Typography tag="p">
							If we believe a story is not applicable, we will escalate the review to one of our trauma experts. While we
							want to represent a diversity of experiences, we want to be sure we are focusing on survivors of trauma and violence.
						</Typography>

						{/* Recognizing and Acting on Suicidality */}
						<br />
						<Typography tag="h2" weight="bold">Recognizing and Acting on Suicidality</Typography>
						<Typography tag="p">
							Survivors of sexual violence are often at higher risk of experiencing various mental health concerns including
							suicidality. As reviewers, we work very closely with our trauma experts to complete the review, and will send
							additional resources to the survivor via email where appropriate.
						</Typography>
						<br />

						{/* Elements of Suicidality */}
						<Typography tag="h4" weight="bold">Elements of Suicidality</Typography>
						<ul>
							<li>
								<span className="emphasis-holder">Ideation</span>
								{' '}
								—
								frequency, intensity, duration
							</li>
							<li>
								<span className="emphasis-holder">Plan</span>
								{' '}
								—
								timing, location, lethality, availability, preparatory acts
							</li>
							<li>
								<span className="emphasis-holder">Behaviors</span>
								{' '}
								—
								past attempts, aborted attempts, rehearsals (tying noose, loading gun) vs. non-suicidal self-injurious actions
							</li>
							<li>
								<span className="emphasis-holder">Intent</span>
								{' '}
								—
								extent to which the individual expects to carry out the plan and, two, believes the plan/act to be lethal vs. self-injurious.
							</li>
						</ul>

						{/* What do we do once we read a story that contains a suicidal ideation or statement? */}
						<br />
						<Typography tag="h4" weight="bold">What do we do once we read a story that contains a suicidal ideation or statement?</Typography>
						<ol>
							<li>
								<span className="emphasis-holder">If it is in the low risk category</span>
								<ol type="a">
									<li className="top">
										We flag it with a trigger warning and post the story as usual
									</li>
									<li>
										The survivor will receive a more personalized email with mental health resources
									</li>
								</ol>
							</li>
							<li>
								<span className="emphasis-holder">If is it in the moderate or high risk category</span>
								<ol type="a">
									<li className="top">
										We do not post the story
									</li>
									<li>
										We escalate the story to our trauma experts to review
									</li>
									<li>
										The survivor will receive a more personalized email with mental health resources
									</li>
								</ol>
							</li>
						</ol>

						{/* Recognizing and Acting on Homicidality */}
						<br />
						<Typography tag="h2" weight="bold">Recognizing and Acting on Homicidality</Typography>
						<Typography tag="p">
							Although unlikely, it is possible that survivors of sexual violence may express wanting to injure/hurt others
							through our platform. This is something we want to take seriously. As reviewers, we work very closely with our
							trauma experts to complete the review and will often send additional resources to the survivor via email.
						</Typography>
						<br />

						{/* Elements of Homicidality */}
						<Typography tag="h4" weight="bold">Elements of Homicidality</Typography>
						<ul>
							<li>
								<span className="emphasis-holder">Beliefs</span>
								{' '}
								—
								If the survivor mentions hateful beliefs about an individual or group
							</li>
							<li>
								<span className="emphasis-holder">Plan</span>
								{' '}
								—
								The survivor mentions a specific plan to harm someone else
							</li>
							<li>
								<span className="emphasis-holder">Available means and opportunity</span>
								{' '}
								—
								If the individual mentions actually having access to a weapon, especially a gun, and has expressed
								homicidal intent and is able to reach the victim without much difficulty, there is a serious risk
							</li>
							<li>
								<span className="emphasis-holder">Past experiences with violence</span>
								{' '}
								—
								If the individual mentioned acting upon violent feelings in the past and has a record of attempting to
								kill or hurt others, the risk is great that they will do it again, especially if their past experience
								did not result in a negative outcome. Violations of court orders or parole that have gone unpunished
								are examples of this
							</li>
						</ul>

						{/* What do we do once we read a story that contains a homicidal ideation or statement? */}
						<br />
						<Typography tag="h4" weight="bold">What do we do once we read a story that contains a homicidal ideation or statement?</Typography>
						<ol>
							<li>
								<span className="emphasis-holder">If it is in the low risk category</span>
								<ol type="a">
									<li className="top">
										We flag it with a trigger warning and post the story as usual
									</li>
									<li>
										The survivor will get a more personalized email with mental health resources
									</li>
								</ol>
							</li>
							<li>
								<span className="emphasis-holder">If is it in the moderate or high risk category</span>
								<ol type="a">
									<li className="top">
										We do not post the story
									</li>
									<li>
										We escalate the story to our trauma experts to review
									</li>
									<li>
										The survivor will receive a more personalized email with mental health resources
									</li>
								</ol>
							</li>
						</ol>

						{/* Reporting Sexual Abuse of Minors */}
						<br />
						<Typography tag="h2" weight="bold">Reporting Sexual Abuse of Minors</Typography>
						<Typography tag="p">
							Our terms and conditions state that individuals must be over the age of 18 to post a story on our page. They also
							state that we cannot accept stories relating to child sexual abuse. Where a person submits a story of this kind despite
							the terms outlined, we will follow these review guidelines:
						</Typography>
						<ol>
							<li>
								We-Speak cannot accept stories from those aged under 18 years, or stories relating to Child Sexual Abuse. If you
								are under 18 and submit a story, or if your story relates to Child Sexual Abuse, we will be unable to publish it.
							</li>
							<li>
								If stories submitted contain identifiable information about yourself and/or another person, then we have a duty
								to report any child protection concern to appropriate authorities, as per the Children First National Guidance
								for the Protection and Welfare of Children 2017.
							</li>
						</ol>

						{/* A note on language */}
						<br />
						<Typography tag="p">
							A note on language: We know that language is important, and people feel differently about the terms used to describe
							people who have experienced sexual violence. Throughout our We-Speak platform, where space is limited, we will be
							using ‘survivor’, but we acknowledge that not everyone will identify with this term.
						</Typography>

						{/* Summary */}
						<br />
						<Typography tag="p">
							Thank you for being part of the We-Speak Community. We have carefully designed and reviewed these guidelines to
							create the safest environment possible for survivors while emphasizing the power of survivor storytelling. If you
							have any questions about these guidelines, please contact us at info@weconsent.ie.
						</Typography>

						{/* Updated Date */}
						<Typography tag="p">
							<i>Updated May 31, 2025</i>
						</Typography>

					</S.Wrapper>
				</LegalNavigation>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

WeSpeakReviewGuidelines.propTypes = {
	meta: PropTypes.shape()
};
WeSpeakReviewGuidelines.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default WeSpeakReviewGuidelines;
